import { useState, useRef } from "react";
import { API_URLS } from "../../../apiUrl";
import BoroughDropdown from "../../customHooks/boroughDropdown";
import axiosInstance from "../../../components/axiosInstance";

const AddWard = ({ muniCipalityName, muniCipalityId }: any) => {
  const [formData, setFormData] = useState<any>({
    municipalityTbl_id: muniCipalityId,
    borough_tbl_id: "",
    wardno: "",
    file: null,
  });

  const [errors, setErrors] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [dataExistMessage, setDataExistMessage] = useState<string>("");
  const [dataFailedMessage, setDataFailedMessage] = useState<string>("");
  const [wardNames, setWardNames] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<any>(null);
  const [isSampleDownloaded, setIsSampleDownloaded] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const [
    ,
    ,
    ,
    boroughList,
    ,
    ,
  ] = BoroughDropdown(muniCipalityId);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && inputValue.trim() !== "") {
      const newWard = inputValue.trim();
      if (!wardNames.includes(newWard)) {
        setWardNames([...wardNames, newWard]);
      }
      setInputValue("");
      event.preventDefault();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | any
  ) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData({
        ...formData,
        file: files ? files[0] : null,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      municipalityTbl_id: muniCipalityId,
      borough_tbl_id: "",
      wardno: "",
      file: null,
    });
    setWardNames([]);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.borough_tbl_id.trim()) {
      newErrors.borough_tbl_id = "Borough is required";
    }

    if (wardNames.length === 0) {
      newErrors.wardNames = "Ward Names are required";
    } else {
      const textSpaceNumberRegex = /^[A-Za-z0-9\s]+$/;
      if (!wardNames.every(ward => textSpaceNumberRegex.test(ward))) {
        newErrors.wardNames = "Ward Name must only contain letters, numbers, and spaces.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updatedFormData = {
      ...formData,
      wardno: wardNames.join("#"),
    };

    if (validateForm()) {
      try {
        const response = await axiosInstance.post(`${API_URLS.addWard}`, updatedFormData);

        if (response.data.data.addedwards.length > 0) {
          const wardNumbers = response.data.data.addedwards.map((ward: any) => ward.ward_no).join(', ');
          setSuccessMessage(`Ward inserted successfully: ${wardNumbers}`);
        } else if (response.data.data.duplicatewards.length > 0 && response.data.data.addedwards.length == 0) {
          setDataExistMessage("Wards already exist for this municipality!");
        } else {
          setDataFailedMessage("Ward add failed!");
        }

        setTimeout(() => {
          setSuccessMessage("");
          setDataExistMessage("");
          setDataFailedMessage("");
        }, 5000);

        resetForm();
      } catch (error) {
        console.error("Error while adding ward:", error);
        setDataFailedMessage("Ward Add failed.");
        setTimeout(() => {
          setDataFailedMessage("");
        }, 5000);
      }
    }
  };

  const handleCSVUpload = async () => {
    if (!formData.file) {
      setDataFailedMessage("Please select a file.");
      return;
    }

    const formDataForUpload = new FormData();
    formDataForUpload.append("ward_bulk_data", formData.file);

    try {
      const response = await axiosInstance.post(`${API_URLS.BulkWardData}`, formDataForUpload);

      if (response.data.message) {
        setSuccessMessage("CSV uploaded successfully!");
      } else {
        setDataFailedMessage("CSV upload failed.");
      }
    } catch (error) {
      console.error("Error while uploading CSV:", error);
      setDataFailedMessage("CSV upload failed.");
    }

    setTimeout(() => {
      setSuccessMessage("");
      setDataFailedMessage("");
    }, 5000);
  };

  const downloadSampleCSV = () => {
    if (!formData.borough_tbl_id) {
      alert("Please select a borough before downloading the sample CSV.");
      return;
    }

    const sampleData = [
      { municipality: muniCipalityId, borough: formData.borough_tbl_id }
    ];

    const csvContent = [
      ["municipality", "borough_id", "ward_no"],
      ...sampleData.map((item) => [item.municipality, item.borough, "ward 001"])
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "ward_bulk_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsSampleDownloaded(true);
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Ward</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <div>
                <label
                  htmlFor="property_type_id"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Municipality <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={muniCipalityName}
                    id="type_description_name"
                    name="type_description_name"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    disabled
                  />
                </div>
              </div>

              <div>
                <label htmlFor="borough_tbl_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Borough <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <select
                    id="borough_tbl_id"
                    name="borough_tbl_id"
                    value={formData.borough_tbl_id}
                    onChange={handleChange}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  >
                    <option value="">Select Borough</option>
                    {boroughList?.map((borough: any) => (
                      <option key={borough.borough_primary_id} value={borough.borough_primary_id}>
                        {borough.borough_name}
                      </option>
                    ))}
                  </select>

                  {errors.borough_tbl_id && (
                    <p className="text-red-600 text-sm mt-1">{errors.borough_tbl_id}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="file-upload" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Bulk Upload (CSV, Excel)
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    id="file-upload"
                    name="file-upload"
                    accept=".csv, .xls, .xlsx"
                    onChange={handleChange}
                    disabled={!isSampleDownloaded || wardNames.length > 0}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  />
                  {formData.file && (
                    <span className="ml-4 text-gray-700 dark:text-gray-400">
                      {formData.file.name}
                    </span>
                  )}
                </div>
              </div>

              <div className="col-span-2">
                <label
                  htmlFor="wardNames"
                  className="flex gap-1 mb-2 text-sm font-medium text-gray-900 dark:text-white leading-3"
                >
                  Ward Names <span className="text-red-600">*</span>
                  
                <div className="text-red-500 block text-xs font-medium leading-3">(Type the ward names and press Enter)</div>
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {wardNames.map((ward: any, index: any) => (
                      <div
                        key={index}
                        className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1"
                      >
                        {ward}
                        <button
                          type="button"
                          onClick={() => setWardNames(wardNames.filter((name: any) => name !== ward))}
                          className="text-red-500 text-sm"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>

                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Type a ward name and press Enter"
                  />
                </div>

                {errors.wardNames && (
                  <p className="text-red-600 text-sm mt-1">{errors.wardNames}</p>
                )}
              </div>

              <div className="lg:col-span-2 sm:col-span-2 mt-3">
                <div className="flex gap-x-2">
                  <button
                    type="button"
                    onClick={downloadSampleCSV}
                    disabled={wardNames.length > 0 || inputValue != null || isSampleDownloaded == true}
                    className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-orange-600 dark:hover:bg-orange-600 dark:focus:ring-orange-600 disabled:opacity-50"
                  >
                    Download Sample CSV
                  </button>

                  <button
                    type="button"
                    onClick={handleCSVUpload}
                    disabled={!formData.borough_tbl_id || !isSampleDownloaded || !formData.file}
                    className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-orange-600 dark:hover:bg-orange-600 dark:focus:ring-orange-600 disabled:opacity-50"
                  >
                    Upload CSV
                  </button>

                  <button
                    type="submit"
                    disabled={formData.file != null}
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
              <span>{successMessage}</span>
              <button onClick={() => setSuccessMessage("")} className="text-white">
                &times;
              </button>
            </div>
          )}
          {dataExistMessage && (
            <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg">
              <span>{dataExistMessage}</span>
            </div>
          )}
          {dataFailedMessage && (
            <div className="mt-4 p-4 bg-red-600 text-white rounded-lg flex justify-between items-center">
              <span>{dataFailedMessage}</span>
              <button
                onClick={() => setDataFailedMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AddWard;